import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Select, Card, Row, Col, Typography, Spin } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { reportFiltersSaga } from "./saga";
import { useAuth } from "../../../contexts/auth";
import * as selectors from "./selectors";

const { Text } = Typography;

export default function ReportsFilter({
  selectedClients,
  selectedProjects,
  selectedCommodities,
  selectedStates,
  selectedDistricts,
  selectedSamplers,
  selectedLabs,
  handleClientChange,
  handleProjectChange,
  handleCommodityChange,
  handleStateChange,
  handleDistrictChange,
  handleSamplerChange,
  handleLabChange,
}) {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: reportFiltersSaga });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuth();
  const user = auth.loggedInUser;

  const userRole = user?.role;
  const locationFilters = location.state?.filters;
  const clients = useSelector(selectors.selectClients);
  const projects = useSelector(selectors.selectProjects);
  const commodities = useSelector(selectors.selectCommodities);
  const states = useSelector(selectors.selectStates);
  const districts = useSelector(selectors.selectDistricts);
  const rawProjects = useSelector(selectors.selectRawProjects);
  const rawSamplers = useSelector(selectors.selectRawSamplers);
  const rawLabs = useSelector(selectors.selectRawLabs);
  const samplers = useSelector(selectors.selectSamplers);
  const filteredLabs = useSelector(
    selectors.selectFilteredLabs(selectedStates || [])
  );

  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isDistrictsLoading, setIsDistrictsLoading] = useState(false);
  const [isSamplersLoading, setIsSamplersLoading] = useState(false);
  const [isLabsLoading, setIsLabsLoading] = useState(false);

  // Track initialization phases
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);

  // Track what we've already fetched to prevent duplicates
  const lastFetchedProjects = useRef('');
  const lastFetchedDistricts = useRef('');
  const lastFetchedSamplers = useRef('');

  const previousStatesRef = useRef([]);
  const previousDistrictsRef = useRef([]);
  const isFirstRender = useRef(true);

  // Memoize selected arrays
  const memoizedSelectedClients = useMemo(
    () => selectedClients || [],
    [selectedClients]
  );
  const memoizedSelectedProjects = useMemo(
    () => selectedProjects || [],
    [selectedProjects]
  );
  const memoizedSelectedCommodities = useMemo(
    () => selectedCommodities || [],
    [selectedCommodities]
  );
  const memoizedSelectedStates = useMemo(
    () => selectedStates || [],
    [selectedStates]
  );
  const memoizedSelectedDistricts = useMemo(
    () => selectedDistricts || [],
    [selectedDistricts]
  );
  const memoizedSelectedSamplers = useMemo(
    () => selectedSamplers || [],
    [selectedSamplers]
  );
  const memoizedSelectedLabs = useMemo(
    () => selectedLabs || [],
    [selectedLabs]
  );

  // Initialize basic data (always needed)
  useEffect(() => {
    dispatch(actions.fetchClients());
    dispatch(actions.fetchCommodities());
    dispatch(actions.fetchStates());
    setIsLabsLoading(true);
    dispatch(actions.fetchLabs());
  }, [dispatch]);

  // Mark when basic data is ready
  useEffect(() => {
    if (states && !hasFetchedInitialData) {
      setHasFetchedInitialData(true);
    }
  }, [states, hasFetchedInitialData]);


  // Combined Projects fetch (initialization + user interaction)
  useEffect(() => {
    let clientIdsToFetch = [];
    let shouldFetch = false;

    // During initialization - use location filters
    if (hasFetchedInitialData && !hasInitialized && locationFilters?.companyIds?.length > 0) {
      clientIdsToFetch = locationFilters.companyIds;
      shouldFetch = true;
    }
    // After initialization - use current selections
    else if (hasInitialized && memoizedSelectedClients.length > 0) {
      clientIdsToFetch = memoizedSelectedClients;
      shouldFetch = true;
    }
    // Clear projects if no clients selected (only after initialization)
    else if (hasInitialized && memoizedSelectedClients.length === 0) {
      dispatch(actions.clearProjects());
      lastFetchedProjects.current = '';
      return;
    }

    if (shouldFetch) {
      const fetchKey = JSON.stringify(clientIdsToFetch.sort());
      
      // Only fetch if different from last fetch
      if (fetchKey !== lastFetchedProjects.current) {
        setIsProjectsLoading(true);
        const query = clientIdsToFetch
          .map((id) => `companyId=${id}`)
          .join("&");
        dispatch(actions.fetchProjects({ query }));
        lastFetchedProjects.current = fetchKey;
      }
    }
  }, [
    hasFetchedInitialData,
    hasInitialized,
    locationFilters?.companyIds,
    memoizedSelectedClients,
    dispatch
  ]);

  // Combined Districts fetch (initialization + user interaction)
  useEffect(() => {
    let stateIdsToFetch = [];
    let shouldFetch = false;

    // During initialization - use location filters
    if (hasFetchedInitialData && !hasInitialized && locationFilters?.stateIds?.length > 0) {
      stateIdsToFetch = locationFilters.stateIds;
      shouldFetch = true;
    }
    // After initialization - use current selections
    else if (hasInitialized && memoizedSelectedStates.length > 0) {
      stateIdsToFetch = memoizedSelectedStates;
      shouldFetch = true;
    }
    // Clear districts if no states selected (only after initialization)
    else if (hasInitialized && memoizedSelectedStates.length === 0) {
      dispatch(actions.clearDistricts());
      lastFetchedDistricts.current = '';
      return;
    }

    if (shouldFetch) {
      const fetchKey = JSON.stringify(stateIdsToFetch.sort());
      
      // Only fetch if different from last fetch
      if (fetchKey !== lastFetchedDistricts.current) {
        setIsDistrictsLoading(true);
        dispatch(actions.fetchDistricts({ stateIds: stateIdsToFetch }));
        lastFetchedDistricts.current = fetchKey;
      }
    }
  }, [
    hasFetchedInitialData,
    hasInitialized,
    locationFilters?.stateIds,
    memoizedSelectedStates,
    dispatch
  ]);

  // Combined Samplers fetch (initialization + user interaction)
  const districtsLoaded = useMemo(() => districts !== undefined, [districts]);
  
  useEffect(() => {
    let stateIdsToFetch = [];
    let districtIdsToFetch = [];
    let shouldFetch = false;

    // During initialization - use location filters
    if (
      hasFetchedInitialData &&
      !hasInitialized &&
      locationFilters?.stateIds?.length > 0 &&
      locationFilters?.districtIds?.length > 0 &&
      districtsLoaded
    ) {
      stateIdsToFetch = locationFilters.stateIds;
      districtIdsToFetch = locationFilters.districtIds;
      shouldFetch = true;
    }
    // After initialization - use current selections
    else if (
      hasInitialized &&
      memoizedSelectedStates.length > 0 &&
      memoizedSelectedDistricts.length > 0
    ) {
      stateIdsToFetch = memoizedSelectedStates;
      districtIdsToFetch = memoizedSelectedDistricts;
      shouldFetch = true;
    }
    // Clear samplers if prerequisites not met (only after initialization)
    else if (
      hasInitialized &&
      (memoizedSelectedStates.length === 0 || memoizedSelectedDistricts.length === 0)
    ) {
      dispatch(actions.clearSamplers());
      lastFetchedSamplers.current = '';
      return;
    }

    if (shouldFetch) {
      const fetchKey = JSON.stringify({
        states: stateIdsToFetch.sort(),
        districts: districtIdsToFetch.sort()
      });
      
      // Only fetch if different from last fetch
      if (fetchKey !== lastFetchedSamplers.current) {
        setIsSamplersLoading(true);
        dispatch(actions.fetchSamplers({
          stateIds: stateIdsToFetch,
          districtIds: districtIdsToFetch,
        }));
        lastFetchedSamplers.current = fetchKey;
      }
    }
  }, [
    hasFetchedInitialData,
    hasInitialized,
    locationFilters?.stateIds,
    locationFilters?.districtIds,
    memoizedSelectedStates,
    memoizedSelectedDistricts,
    districtsLoaded,
    dispatch
  ]);

  // Mark initialization as complete
  useEffect(() => {
    if (hasFetchedInitialData) {
      // No location filters to restore
      if (!locationFilters?.stateIds?.length) {
        setHasInitialized(true);
        return;
      }
      // Has state filters but no district filters
      if (!locationFilters?.districtIds?.length) {
        if (districts !== undefined) {
          setHasInitialized(true);
        }
        return;
      }
      // Has both state and district filters
      if (samplers !== undefined) {
        setHasInitialized(true);
      }
    }
  }, [
    hasFetchedInitialData,
    locationFilters?.stateIds,
    locationFilters?.districtIds,
    districts,
    samplers
  ]);


  useEffect(() => {
    if (projects !== undefined) {
      setIsProjectsLoading(false);
    }
  }, [projects]);

  useEffect(() => {
    if (districts !== undefined) {
      setIsDistrictsLoading(false);
    }
  }, [districts]);

  useEffect(() => {
    if (samplers !== undefined) {
      setIsSamplersLoading(false);
    }
  }, [samplers]);

  useEffect(() => {
    if (rawLabs !== undefined) {
      setIsLabsLoading(false);
    }
  }, [rawLabs]);


  useEffect(() => {
    if (!hasInitialized) return;

    if (
      rawProjects &&
      rawProjects.length > 0 &&
      memoizedSelectedProjects.length > 0
    ) {
      const validProjects = selectors.getValidProjects(
        memoizedSelectedProjects,
        memoizedSelectedClients,
        rawProjects
      );

      if (validProjects.length !== memoizedSelectedProjects.length) {
        handleProjectChange(validProjects);
      }
    }
  }, [
    rawProjects,
    memoizedSelectedClients,
    memoizedSelectedProjects,
    handleProjectChange,
    hasInitialized,
  ]);

  // Smart deselect for districts
  const rawDistricts = useSelector(selectors.selectRawDistricts);
  const rawStates = useSelector(selectors.selectRawStates);

  useEffect(() => {
    if (!hasInitialized) return;

    const currentStatesString = JSON.stringify(memoizedSelectedStates.sort());
    const previousStatesString = JSON.stringify(
      previousStatesRef.current.sort()
    );

    if (
      currentStatesString !== previousStatesString &&
      memoizedSelectedDistricts.length > 0
    ) {
      if (
        rawDistricts &&
        rawDistricts.length > 0 &&
        rawStates &&
        rawStates.length > 0
      ) {
        const validDistricts = selectors.getValidDistricts(
          memoizedSelectedDistricts,
          memoizedSelectedStates,
          rawDistricts,
          rawStates
        );

        if (validDistricts.length !== memoizedSelectedDistricts.length) {
          handleDistrictChange(validDistricts);
        }
      } else if (memoizedSelectedStates.length === 0) {
        handleDistrictChange([]);
      }
    }

    previousStatesRef.current = [...memoizedSelectedStates];
  }, [
    memoizedSelectedStates,
    memoizedSelectedDistricts,
    rawDistricts,
    rawStates,
    handleDistrictChange,
    hasInitialized,
  ]);

  // Smart deselect for samplers
  useEffect(() => {
    if (!hasInitialized) return;

    const currentStatesString = JSON.stringify(memoizedSelectedStates.sort());
    const currentDistrictsString = JSON.stringify(
      memoizedSelectedDistricts.sort()
    );
    const previousStatesString = JSON.stringify(
      previousStatesRef.current.sort()
    );
    const previousDistrictsString = JSON.stringify(
      previousDistrictsRef.current.sort()
    );

    if (
      (currentStatesString !== previousStatesString ||
        currentDistrictsString !== previousDistrictsString) &&
      memoizedSelectedSamplers.length > 0
    ) {
      if (rawSamplers && rawSamplers.length > 0) {
        const validSamplers = selectors.getValidSamplers(
          memoizedSelectedSamplers,
          memoizedSelectedStates,
          memoizedSelectedDistricts,
          rawSamplers
        );

        if (validSamplers.length !== memoizedSelectedSamplers.length) {
          handleSamplerChange(validSamplers);
        }
      } else if (
        memoizedSelectedStates.length === 0 ||
        memoizedSelectedDistricts.length === 0
      ) {
        handleSamplerChange([]);
      }
    }

    previousDistrictsRef.current = [...memoizedSelectedDistricts];
  }, [
    memoizedSelectedStates,
    memoizedSelectedDistricts,
    memoizedSelectedSamplers,
    rawSamplers,
    handleSamplerChange,
    hasInitialized,
  ]);

  // Smart deselect for labs
  useEffect(() => {
    if (!hasInitialized) return;

    if (rawLabs && rawLabs.length > 0 && memoizedSelectedLabs.length > 0) {
      const validLabs = selectors.getValidLabs(
        memoizedSelectedLabs,
        memoizedSelectedStates,
        rawLabs
      );

      if (validLabs.length !== memoizedSelectedLabs.length) {
        handleLabChange(validLabs);
      }
    }
  }, [
    rawLabs,
    memoizedSelectedStates,
    memoizedSelectedLabs,
    handleLabChange,
    hasInitialized,
  ]);

  const handleClientChangeWrapper = useCallback(
    (newSelectedClients) => {
      handleClientChange(newSelectedClients);

      if (
        rawProjects &&
        rawProjects.length > 0 &&
        memoizedSelectedProjects.length > 0
      ) {
        const validProjects = selectors.getValidProjects(
          memoizedSelectedProjects,
          newSelectedClients,
          rawProjects
        );

        if (validProjects.length !== memoizedSelectedProjects.length) {
          handleProjectChange(validProjects);
        }
      }
    },
    [
      handleClientChange,
      rawProjects,
      memoizedSelectedProjects,
      handleProjectChange,
    ]
  );

  const handleStateChangeWrapper = useCallback(
    (newSelectedStates) => {
      handleStateChange(newSelectedStates);
    },
    [handleStateChange]
  );

  const handleDistrictChangeWrapper = useCallback(
    (newSelectedDistricts) => {
      handleDistrictChange(newSelectedDistricts);
    },
    [handleDistrictChange]
  );

  // Memoize the filters object
  const currentFilters = useMemo(
    () => ({
      companyIds: memoizedSelectedClients,
      projectCodeIds: memoizedSelectedProjects,
      commodityIds: memoizedSelectedCommodities,
      stateIds: memoizedSelectedStates,
      districtIds: memoizedSelectedDistricts,
      samplerIds: memoizedSelectedSamplers,
      labIds: memoizedSelectedLabs,
    }),
    [
      memoizedSelectedClients,
      memoizedSelectedProjects,
      memoizedSelectedCommodities,
      memoizedSelectedStates,
      memoizedSelectedDistricts,
      memoizedSelectedSamplers,
      memoizedSelectedLabs,
    ]
  );

  // Update URL state
  useEffect(() => {
    if (hasInitialized) {
      history.replace(location.pathname + location.search, {
        filters: currentFilters,
      });
    }
  }, [
    currentFilters,
    history,
    location.pathname,
    location.search,
    hasInitialized,
  ]);

  // Mark first render as complete
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <Card>
      {/* Row 1 - Client, Project, Commodity */}
      <Row gutter={[16, 16]} align="middle">
        {userRole !== "company_admin" && (
          <Col xs={24} sm={12} md={8}>
            <Text strong>Client</Text>
            <Select
              mode="multiple"
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                `${omittedValues.length} ${
                  omittedValues.length > 1 ? "clients" : "client"
                } selected`
              }
              value={memoizedSelectedClients}
              placeholder="Select Client"
              style={{ width: "100%", marginTop: 4 }}
              options={clients || []}
              onChange={handleClientChangeWrapper}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
          </Col>
        )}

        <Col xs={24} sm={12} md={8}>
          <Text strong>Project</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "projects" : "project"
              } selected`
            }
            placeholder="Select Project"
            style={{ width: "100%", marginTop: 4 }}
            options={projects || []}
            disabled={!memoizedSelectedClients?.length}
            loading={isProjectsLoading}
            notFoundContent={
              isProjectsLoading ? <Spin size="small" /> : "No projects found"
            }
            value={memoizedSelectedProjects}
            onChange={handleProjectChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Text strong>Commodity</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "commodities" : "commodity"
              } selected`
            }
            value={memoizedSelectedCommodities}
            placeholder="Select Commodity"
            style={{ width: "100%", marginTop: 4 }}
            options={commodities || []}
            onChange={handleCommodityChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>
      </Row>

      {/* Row 2 - State, District, Sampler, Lab */}
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={6}>
          <Text strong>State</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "states" : "state"
              } selected`
            }
            value={memoizedSelectedStates}
            placeholder="Select State"
            style={{ width: "100%", marginTop: 4 }}
            options={states || []}
            onChange={handleStateChangeWrapper}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text strong>District</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "districts" : "district"
              } selected`
            }
            value={memoizedSelectedDistricts}
            placeholder="Select District"
            style={{ width: "100%", marginTop: 4 }}
            options={districts || []}
            disabled={!memoizedSelectedStates?.length}
            loading={isDistrictsLoading}
            notFoundContent={
              isDistrictsLoading ? <Spin size="small" /> : "No districts found"
            }
            onChange={handleDistrictChangeWrapper}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text strong>Sampler</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "samplers" : "sampler"
              } selected`
            }
            value={memoizedSelectedSamplers}
            placeholder="Select Sampler"
            style={{ width: "100%", marginTop: 4 }}
            options={samplers || []}
            loading={isSamplersLoading}
            notFoundContent={
              isSamplersLoading ? <Spin size="small" /> : "No samplers found"
            }
            onChange={handleSamplerChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text strong>Lab</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "labs" : "lab"
              } selected`
            }
            value={memoizedSelectedLabs}
            placeholder="Select Lab"
            style={{ width: "100%", marginTop: 4 }}
            options={(filteredLabs || []).map(({ value, label }) => ({
              value,
              label,
            }))}
            loading={isLabsLoading}
            notFoundContent={
              isLabsLoading ? <Spin size="small" /> : "No labs found"
            }
            onChange={handleLabChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>
      </Row>
    </Card>
  );
}